import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminNav, { AdminHeader } from "@/components/AdminNav";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminEmails() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/emails")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load email log"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openTestDialog = () => {
    setTestTo("");
    setDialogOpen(true);
  };

  // The ONLY code path that dispatches through Resend.
  // No network call happens until the admin explicitly clicks the
  // "SEND ONE TEST EMAIL" button inside the dialog.
  const sendResendTest = async () => {
    const to = testTo.trim().toLowerCase();
    if (!EMAIL_RE.test(to)) {
      toast.error("Please enter a valid recipient email address.");
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/emails/test", {
        event: "TRANSACTIONAL_TEST",
        to,
        provider: "resend",
      });
      if (data.ok) {
        toast.success("Test email sent successfully through Resend.");
        setDialogOpen(false);
        load();
      } else {
        // Provider-side failure — safe generic message, no secret exposure.
        toast.error("Resend did not accept the test message. See delivery log for details.");
      }
    } catch (err) {
      const status = err?.response?.status;
      // Distinct-but-safe messages for the known error surfaces.
      if (status === 400) {
        toast.error("Server is missing the Resend API key. Contact ops.");
      } else if (status === 401 || status === 403) {
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error("We couldn't send the test right now. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-tight py-8" data-testid="admin-emails-page">
      <AdminNav />
      <AdminHeader title="EMAIL / NOTIFICATIONS"
                   subtitle="Delivery log and provider status" />
      {loading || !data ? (
        <div className="font-mono text-zinc-500">LOADING...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Stat label="TOTAL" value={data.totals.total} />
            <Stat label="SENT" value={data.totals.sent} color="#22c55e" />
            <Stat label="QUEUED" value={data.totals.queued} color="#eab308" />
            <Stat label="FAILED" value={data.totals.failed} color="#ef4444" />
            <Stat label="NOT CONNECTED" value={data.totals.not_connected} color="#a855f7" />
          </div>

          <div className="border border-zinc-800 bg-[#0a0a0c] rounded-sm p-4 mb-6"
               data-testid="email-provider-health">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase mb-2">
              PROVIDER
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-mono text-sm text-white">
                {(data.provider?.provider || "unknown").toUpperCase()}
              </span>
              <span className="font-mono text-xs px-2 py-0.5 border rounded-sm border-zinc-700 text-zinc-300">
                {(data.provider?.state || "unknown").replace(/_/g, " ")}
              </span>
              <button
                onClick={openTestDialog}
                data-testid="email-send-test-btn"
                className="ml-auto bg-[#FF5C00] hover:bg-[#FF7A33] text-black font-mono text-xs tracking-widest uppercase px-3 py-1.5 rounded-sm">
                Send Test
              </button>
            </div>
            {data.provider?.note && (
              <p className="text-xs text-zinc-500 mt-2">{data.provider.note}</p>
            )}
          </div>

          <div className="border border-zinc-800 bg-[#0a0a0c] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase border-b border-zinc-900">
                  <th className="p-3 text-left">Event</th>
                  <th className="p-3 text-left">Recipient</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Provider</th>
                  <th className="p-3 text-left">Created</th>
                  <th className="p-3 text-left">Sent</th>
                  <th className="p-3 text-left">Failure</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-zinc-500 font-mono text-xs"
                        data-testid="emails-empty">
                      No email events yet.
                    </td>
                  </tr>
                ) : data.items.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-900 hover:bg-black/40"
                      data-testid={`email-row-${r.id}`}>
                    <td className="p-3 font-mono text-[11px] text-white">
                      {r.event_type}
                      {r.is_test && (
                        <span className="ml-2 text-[9px] px-1.5 py-0.5 border border-yellow-500/40 text-yellow-400 rounded-sm">
                          TEST
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-zinc-300">{r.recipient_masked}</td>
                    <td className="p-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="p-3 font-mono text-[11px] text-zinc-400">{r.provider}</td>
                    <td className="p-3 font-mono text-[11px] text-zinc-500">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-zinc-500">
                      {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-red-400">
                      {r.failure_reason || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="send-test-dialog"
          className="bg-[#0a0a0c] border border-zinc-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-white">
              Send Transactional Email Test
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Nothing is sent until you press{" "}
              <span className="font-mono text-white">SEND ONE TEST EMAIL</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <label className="block">
              <span className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase block mb-1.5">
                Recipient<span className="text-[#FF5C00]">*</span>
              </span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                disabled={sending}
                placeholder="you@example.com"
                data-testid="send-test-to-input"
                className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF5C00] disabled:opacity-60"
              />
            </label>

            <MetaRow label="Provider" value="Resend" testId="send-test-provider" />
            <MetaRow
              label="From"
              value="Interstate PC <no-reply@send.interstate-pc.com>"
              testId="send-test-from"
            />
            <MetaRow
              label="Reply-To"
              value="sales@interstate-pc.com"
              testId="send-test-reply-to"
            />

            <div
              className="flex items-start gap-2 border border-yellow-500/30 bg-yellow-500/5 rounded-sm p-3"
              data-testid="send-test-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-yellow-400 flex-shrink-0" />
              <p className="text-xs text-yellow-100/90 leading-relaxed">
                This sends one <strong>REAL</strong> email through Resend.
                It will be recorded as a <strong>TEST</strong>.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setDialogOpen(false)}
              disabled={sending}
              data-testid="send-test-cancel-btn"
              className="border border-zinc-700 hover:bg-zinc-900 text-zinc-300 font-mono text-xs tracking-widest uppercase px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
              Cancel
            </button>
            <button
              type="button"
              onClick={sendResendTest}
              disabled={sending}
              data-testid="send-test-confirm-btn"
              className="bg-[#FF5C00] hover:bg-[#FF7A33] disabled:bg-zinc-700 disabled:cursor-not-allowed text-black font-mono text-xs tracking-widest uppercase font-bold px-4 py-2 rounded-sm transition-colors">
              {sending ? "Sending..." : "Send One Test Email"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaRow({ label, value, testId }) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase w-20 flex-shrink-0">
        {label}
      </span>
      <span
        data-testid={testId}
        className="font-mono text-xs text-white bg-black border border-zinc-800 rounded-sm px-2 py-1 flex-1 truncate">
        {value}
      </span>
    </div>
  );
}

function Stat({ label, value, color = "#FF5C00" }) {
  return (
    <div className="border border-zinc-800 bg-[#0a0a0c] p-4 rounded-sm">
      <div className="font-mono text-[10px] tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-2xl font-bold mt-2" style={{ color }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: "bg-green-500/10 text-green-400 border-green-500/30",
    queued: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    failed: "bg-red-500/10 text-red-400 border-red-500/30",
    not_connected: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  };
  const cls = map[status] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/30";
  return (
    <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border rounded-sm ${cls}`}>
      {(status || "unknown").replace(/_/g, " ")}
    </span>
  );
}
